import * as THREE from 'three';
import { Reflector  } from './utils/Reflector.js';
import { MuJoCoDemo } from './main.js';
import { keyboardController } from './utils/KeyboardControl.js';
import { getSceneManager } from './utils/SceneManager.js';
import { policyController } from './policy/PolicyController.js';

/**
 * Load a modular scene (environment + robot + objects)
 * @param {MuJoCoDemo} context - Demo context
 * @param {string} envName - Environment name (e.g., 'tabletop')
 * @param {string} robotName - Robot name (e.g., 'xlerobot', 'SO101', 'panda')
 */
export async function loadModularScene(context, envName, robotName) {
  const sceneManager = getSceneManager(context.mujoco);

  // Delete the old scene
  context.scene.remove(context.scene.getObjectByName("MuJoCo Root"));

  // Load modular scene - this creates merged XML
  const scenePath = await sceneManager.loadModularScene(envName, robotName);

  // Update params for compatibility
  context.params.scene = scenePath;
  context.params.robot = robotName;
  context.params.environment = envName;

  // Load the merged scene using existing loading function
  [context.model, context.data, context.bodies, context.lights] =
    await loadSceneFromURL(context.mujoco, scenePath, context);

  context.mujoco.mj_forward(context.model, context.data);

  // Run update callbacks (support async)
  for (let i = 0; i < context.updateGUICallbacks.length; i++) {
    await context.updateGUICallbacks[i](context.model, context.data, context.params);
  }

  return sceneManager;
}

export async function reloadFunc() {
  // Reload the current scene
  const envName = this.params.environment || 'tabletop';
  const robotName = this.params.robot || 'xlerobot';
  const sceneManager = getSceneManager(this.mujoco);

  this.scene.remove(this.scene.getObjectByName("MuJoCo Root"));

  // Handle custom SPZ mode
  if (sceneManager.hasCustomSpz() && envName === 'custom_spz') {
    await sceneManager.loadCustomSpzWithRobot(robotName);
  } else if (sceneManager.isUploadedRobot(robotName)) {
    // Handle uploaded robot reload
    await sceneManager.reloadUploadedRobot(envName);
  } else {
    // Normal robot reload
    await sceneManager.loadModularScene(envName, robotName);
  }

  this.params.scene = sceneManager.scenePath;
  [this.model, this.data, this.bodies, this.lights] =
    await loadSceneFromURL(this.mujoco, sceneManager.scenePath, this);
  this.mujoco.mj_forward(this.model, this.data);

  // Run update callbacks (support async)
  for (let i = 0; i < this.updateGUICallbacks.length; i++) {
    await this.updateGUICallbacks[i](this.model, this.data, this.params);
  }
}

/** @param {MuJoCoDemo} parentContext*/
export async function setupGUI(parentContext) {

  // Make sure we reset the camera when the scene is changed or reloaded.
  parentContext.updateGUICallbacks.length = 0;
  parentContext.updateGUICallbacks.push((model, data, params) => {
    // TODO: Use free camera parameters from MuJoCo
    parentContext.camera.position.set(0.5, 1.7, -3);
    parentContext.controls.target.set(0, 0.7, 0);
    parentContext.controls.update(); });

  // Initialize modular scene params
  parentContext.params.environment = parentContext.params.environment || 'tabletop';
  parentContext.params.robot = parentContext.params.robot || 'xlerobot';

  // Helper to remove old MuJoCo scene
  const removeOldScene = () => {
    const oldMujocoRoot = parentContext.scene.getObjectByName("MuJoCo Root");
    if (oldMujocoRoot) {
      parentContext.scene.remove(oldMujocoRoot);
    }
  };

  // Helper to load scene and run callbacks
  const loadSceneAndUpdate = async (scenePath, robotName) => {
    parentContext.params.scene = scenePath;
    [parentContext.model, parentContext.data, parentContext.bodies, parentContext.lights] =
      await loadSceneFromURL(parentContext.mujoco, scenePath, parentContext);
    parentContext.mujoco.mj_forward(parentContext.model, parentContext.data);

    // Run update callbacks (support async)
    for (let i = 0; i < parentContext.updateGUICallbacks.length; i++) {
      await parentContext.updateGUICallbacks[i](parentContext.model, parentContext.data, parentContext.params);
    }

    // Setup keyboard control (async)
    if (keyboardController.hasConfig(robotName)) {
      await keyboardController.enable(robotName, parentContext.model, parentContext.data, parentContext.mujoco);
    }
  };

  // Helper to load custom SPZ 3DGS
  const loadCustomSpz3DGS = async (spzData) => {
    const blob = new Blob([spzData], { type: 'application/octet-stream' });
    const blobUrl = URL.createObjectURL(blob);

    if (parentContext.gsController && parentContext.gsController.enabled) {
      parentContext.gsController.disable();
    }
    if (parentContext.gsController) {
      await parentContext.gsController.enable(blobUrl);
    }
  };

  // Loading overlay helper
  let loadingOverlay = null;
  const showLoading = (message = 'Loading...') => {
    if (!loadingOverlay) {
      loadingOverlay = document.createElement('div');
      loadingOverlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.7);
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        z-index: 9999;
        pointer-events: none;
      `;

      const spinner = document.createElement('div');
      spinner.style.cssText = `
        width: 50px;
        height: 50px;
        border: 4px solid rgba(255, 255, 255, 0.3);
        border-top: 4px solid #fff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
      `;

      const style = document.createElement('style');
      style.textContent = `
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);

      const text = document.createElement('div');
      text.id = 'loading-text';
      text.style.cssText = `
        color: white;
        font-size: 18px;
        margin-top: 20px;
        font-family: Arial, sans-serif;
      `;
      text.textContent = message;

      loadingOverlay.appendChild(spinner);
      loadingOverlay.appendChild(text);
    } else {
      const text = loadingOverlay.querySelector('#loading-text');
      if (text) text.textContent = message;
    }

    if (!document.body.contains(loadingOverlay)) {
      document.body.appendChild(loadingOverlay);
    }
  };

  const hideLoading = () => {
    if (loadingOverlay && document.body.contains(loadingOverlay)) {
      document.body.removeChild(loadingOverlay);
    }
  };

  // Scene loading function for modular scenes
  const loadScene = async () => {
    const sceneManager = getSceneManager(parentContext.mujoco);
    const envName = parentContext.params.environment;
    const robotName = parentContext.params.robot;

    showLoading(`Loading ${robotName}...`);

    try {
      // Custom SPZ mode: reload with new robot
      if (sceneManager.hasCustomSpz() && envName === 'custom_spz') {
        removeOldScene();
        await sceneManager.loadCustomSpzWithRobot(robotName);
        await loadSceneAndUpdate(sceneManager.scenePath, robotName);
        await loadCustomSpz3DGS(sceneManager.getCustomSpzData());
      } else {
        // Clear custom SPZ if switching to a normal environment
        if (sceneManager.hasCustomSpz()) {
          sceneManager.clearCustomSpz();
          if (parentContext.gsController && parentContext.gsController.enabled) {
            parentContext.gsController.disable();
          }
        }

        // Handle uploaded robot or normal robot
        if (sceneManager.isUploadedRobot(robotName)) {
          removeOldScene();
          await sceneManager.reloadUploadedRobot(envName);
          await loadSceneAndUpdate(sceneManager.scenePath, robotName);
        } else {
          await loadModularScene(parentContext, envName, robotName);
        }

        // If 3DGS was enabled, reload with new environment's spz
        if (parentContext.gsController && parentContext.gsController.enabled) {
          parentContext.gsController.disable();
          const spzPath = sceneManager.getSpzPath();
          if (spzPath) {
            await parentContext.gsController.enable(spzPath);
          }
        }
      }
    } finally {
      hideLoading();
    }
  };

  // Add environment selection dropdown
  parentContext.gui.add(parentContext.params, 'environment', {
    "Tabletop": "tabletop",
    "UFC": "UFC"
  }).name('Environment').onChange(loadScene);

  // Add robot selection dropdown
  parentContext.gui.add(parentContext.params, 'robot', {
    "XLeRobot": "xlerobot",
    "SO101": "SO101",
    "Panda": "panda",
    "Humanoid": "humanoid",
    "G1": "g1"
  }).name('Robot').onChange(loadScene);

  // Add upload robot button
  const uploadRobotBtn = {
    uploadRobot: () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.webkitdirectory = true;
      input.multiple = true;
      input.onchange = async (e) => {
        const files = e.target.files;
        if (files.length === 0) return;

        try {
          console.log('Uploading robot folder with', files.length, 'files');
          removeOldScene();

          const sceneManager = getSceneManager(parentContext.mujoco);
          const scenePath = await sceneManager.loadUploadedRobot(files, parentContext.params.environment);
          parentContext.params.robot = sceneManager.currentRobot;

          await loadSceneAndUpdate(scenePath, sceneManager.currentRobot);

          // If in custom SPZ mode, reload the 3DGS
          if (sceneManager.hasCustomSpz()) {
            await loadCustomSpz3DGS(sceneManager.getCustomSpzData());
          }

          console.log('Custom robot loaded successfully');
        } catch (err) {
          console.error('Failed to load uploaded robot:', err);
          alert('Failed to load robot: ' + err.message);
        }
      };
      input.click();
    }
  };
  parentContext.gui.add(uploadRobotBtn, 'uploadRobot').name('Upload Robot Folder');

  // Add upload SPZ button for custom 3DGS scenes
  const uploadSpzBtn = {
    uploadSpz: () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.spz';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
          console.log('Uploading SPZ file:', file.name);
          removeOldScene();

          const sceneManager = getSceneManager(parentContext.mujoco);
          const robotName = parentContext.params.robot;
          const scenePath = await sceneManager.loadCustomSpz(file, robotName);

          parentContext.params.environment = 'custom_spz';
          await loadSceneAndUpdate(scenePath, robotName);

          if (sceneManager.hasCustomSpz()) {
            await loadCustomSpz3DGS(sceneManager.getCustomSpzData());
            console.log('Custom 3DGS loaded from uploaded SPZ');
          }

          console.log('Custom SPZ scene loaded successfully');
        } catch (err) {
          console.error('Failed to load SPZ file:', err);
          alert('Failed to load SPZ: ' + err.message);
        }
      };
      input.click();
    }
  };
  parentContext.gui.add(uploadSpzBtn, 'uploadSpz').name('Upload 3DGS (.spz)');

  // Add upload collision XML button for custom SPZ scenes
  const uploadCollisionBtn = {
    uploadCollision: () => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.xml';
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
          console.log('Uploading collision XML file:', file.name);
          const sceneManager = getSceneManager(parentContext.mujoco);

          // Read the XML content
          const xmlContent = await file.text();
          sceneManager.setCustomCollision(xmlContent);

          // If already in custom_spz mode, reload scene with collision
          if (sceneManager.hasCustomSpz()) {
            removeOldScene();
            const robotName = parentContext.params.robot;
            const scenePath = await sceneManager.loadCustomSpzWithRobot(robotName);
            await loadSceneAndUpdate(scenePath, robotName);
            await loadCustomSpz3DGS(sceneManager.getCustomSpzData());
            console.log('Scene reloaded with collision XML');
          } else {
            console.log('Collision XML stored. Upload a SPZ file to use it.');
            alert('Collision XML loaded. Now upload a SPZ file to create the scene.');
          }
        } catch (err) {
          console.error('Failed to load collision XML:', err);
          alert('Failed to load collision XML: ' + err.message);
        }
      };
      input.click();
    }
  };
  parentContext.gui.add(uploadCollisionBtn, 'uploadCollision').name('Upload Collision (.xml)');

  // Add a help menu.
  // Parameters:
  //  Name: "Help".
  //  When pressed, a help menu is displayed in the top left corner. When pressed again
  //  the help menu is removed.
  //  Can also be triggered by pressing F1.
  // Has a dark transparent background.
  // Has two columns: one for putting the action description, and one for the action key trigger.keyframeNumber
  let keyInnerHTML = '';
  let actionInnerHTML = '';
  const displayHelpMenu = () => {
    if (parentContext.params.help) {
      const helpMenu = document.createElement('div');
      helpMenu.style.position = 'absolute';
      helpMenu.style.top = '10px';
      helpMenu.style.left = '10px';
      helpMenu.style.color = 'white';
      helpMenu.style.font = 'normal 18px Arial';
      helpMenu.style.backgroundColor = 'rgba(0, 0, 0, 0.5)';
      helpMenu.style.padding = '10px';
      helpMenu.style.borderRadius = '10px';
      helpMenu.style.display = 'flex';
      helpMenu.style.flexDirection = 'column';
      helpMenu.style.alignItems = 'center';
      helpMenu.style.justifyContent = 'center';
      helpMenu.style.width = '400px';
      helpMenu.style.height = '400px';
      helpMenu.style.overflow = 'auto';
      helpMenu.style.zIndex = '1000';

      const helpMenuTitle = document.createElement('div');
      helpMenuTitle.style.font = 'bold 24px Arial';
      helpMenuTitle.innerHTML = '';
      helpMenu.appendChild(helpMenuTitle);

      const helpMenuTable = document.createElement('table');
      helpMenuTable.style.width = '100%';
      helpMenuTable.style.marginTop = '10px';
      helpMenu.appendChild(helpMenuTable);

      const helpMenuTableBody = document.createElement('tbody');
      helpMenuTable.appendChild(helpMenuTableBody);

      const helpMenuRow = document.createElement('tr');
      helpMenuTableBody.appendChild(helpMenuRow);

      const helpMenuActionColumn = document.createElement('td');
      helpMenuActionColumn.style.width = '50%';
      helpMenuActionColumn.style.textAlign = 'right';
      helpMenuActionColumn.style.paddingRight = '10px';
      helpMenuRow.appendChild(helpMenuActionColumn);

      const helpMenuKeyColumn = document.createElement('td');
      helpMenuKeyColumn.style.width = '50%';
      helpMenuKeyColumn.style.textAlign = 'left';
      helpMenuKeyColumn.style.paddingLeft = '10px';
      helpMenuRow.appendChild(helpMenuKeyColumn);

      const helpMenuActionText = document.createElement('div');
      helpMenuActionText.innerHTML = actionInnerHTML;
      helpMenuActionColumn.appendChild(helpMenuActionText);

      const helpMenuKeyText = document.createElement('div');
      helpMenuKeyText.innerHTML = keyInnerHTML;
      helpMenuKeyColumn.appendChild(helpMenuKeyText);

      // Close buttom in the top.
      const helpMenuCloseButton = document.createElement('button');
      helpMenuCloseButton.innerHTML = 'Close';
      helpMenuCloseButton.style.position = 'absolute';
      helpMenuCloseButton.style.top = '10px';
      helpMenuCloseButton.style.right = '10px';
      helpMenuCloseButton.style.zIndex = '1001';
      helpMenuCloseButton.onclick = () => {
        helpMenu.remove();
      };
      helpMenu.appendChild(helpMenuCloseButton);

      document.body.appendChild(helpMenu);
    } else {
      document.body.removeChild(document.body.lastChild);
    }
  }
  document.addEventListener('keydown', (event) => {
    if (event.key === 'F1') {
      parentContext.params.help = !parentContext.params.help;
      displayHelpMenu();
      event.preventDefault();
    }
  });
  keyInnerHTML += 'F1<br>';
  actionInnerHTML += 'Help<br>';

  let simulationFolder = parentContext.gui.addFolder("Simulation");

  // Add pause simulation checkbox.
  // Parameters:
  //  Under "Simulation" folder.
  //  Name: "Pause Simulation".
  //  When paused, a "pause" text in white is displayed in the top left corner.
  //  Can also be triggered by pressing the spacebar.
  const pauseSimulation = simulationFolder.add(parentContext.params, 'paused').name('Pause Simulation');
  pauseSimulation.onChange((value) => {
    if (value) {
      const pausedText = document.createElement('div');
      pausedText.style.position = 'absolute';
      pausedText.style.top = '10px';
      pausedText.style.left = '10px';
      pausedText.style.color = 'white';
      pausedText.style.font = 'normal 18px Arial';
      pausedText.innerHTML = 'pause';
      parentContext.container.appendChild(pausedText);
    } else {
      parentContext.container.removeChild(parentContext.container.lastChild);
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.code === 'Space') {
      parentContext.params.paused = !parentContext.params.paused;
      pauseSimulation.setValue(parentContext.params.paused);
      event.preventDefault();
    }
  });
  actionInnerHTML += 'Play / Pause<br>';
  keyInnerHTML += 'Space<br>';

  // Add reload model button.
  // Parameters:
  //  Under "Simulation" folder.
  //  Name: "Reload".
  //  When pressed, calls the reload function.
  //  Can also be triggered by pressing ctrl + L.
  simulationFolder.add({reload: () => { reload(); }}, 'reload').name('Reload');
  document.addEventListener('keydown', (event) => {
    if (event.ctrlKey && event.code === 'KeyL') { reload();  event.preventDefault(); }});
  actionInnerHTML += 'Reload XML<br>';
  keyInnerHTML += 'Ctrl L<br>';

  // Add reset simulation button.
  // Parameters:
  //  Under "Simulation" folder.
  //  Name: "Reset".
  //  When pressed, resets the simulation to the initial state.
  //  Can also be triggered by pressing backspace.
  const resetSimulation = () => {
    parentContext.mujoco.mj_resetData(parentContext.model, parentContext.data);
    parentContext.mujoco.mj_forward(parentContext.model, parentContext.data);
  };
  simulationFolder.add({reset: () => { resetSimulation(); }}, 'reset').name('Reset');
  document.addEventListener('keydown', (event) => {
    if (event.code === 'Backspace') { resetSimulation(); event.preventDefault(); }});
  actionInnerHTML += 'Reset simulation<br>';
  keyInnerHTML += 'Backspace<br>';

  // Add keyframe slider.
  let nkeys = parentContext.model.nkey;
  let keyframeGUI = simulationFolder.add(parentContext.params, "keyframeNumber", 0, nkeys - 1, 1).name('Load Keyframe').listen();
  keyframeGUI.onChange((value) => {
    if (value < parentContext.model.nkey) {
      parentContext.data.qpos.set(parentContext.model.key_qpos.slice(
        value * parentContext.model.nq, (value + 1) * parentContext.model.nq)); }});
  parentContext.updateGUICallbacks.push((model, data, params) => {
    let nkeys = parentContext.model.nkey;
    console.log("new model loaded. has " + nkeys + " keyframes.");
    if (nkeys > 0) {
      keyframeGUI.max(nkeys - 1);
      keyframeGUI.domElement.style.opacity = 1.0;
    } else {
      // Disable keyframe slider if no keyframes are available.
      keyframeGUI.max(0);
      keyframeGUI.domElement.style.opacity = 0.5;
    }
  });

  // Add sliders for ctrlnoiserate and ctrlnoisestd; min = 0, max = 2, step = 0.01.
  simulationFolder.add(parentContext.params, 'ctrlnoiserate', 0.0, 2.0, 0.01).name('Noise rate' );
  simulationFolder.add(parentContext.params, 'ctrlnoisestd' , 0.0, 2.0, 0.01).name('Noise scale');

  let textDecoder = new TextDecoder("utf-8");
  let nullChar    = textDecoder.decode(new ArrayBuffer(1));

  // Add actuator sliders.
  let actuatorFolder = simulationFolder.addFolder("Actuators");
  
  // IK-controlled joint names (these are controlled by keyboard and can't be directly set by sliders)
  const ikControlledJoints = [
    // XLeRobot IK joints
    'Pitch_L', 'Elbow_L', 'Wrist_Pitch_L', 
    'Pitch_R', 'Elbow_R', 'Wrist_Pitch_R',
    // SO101 IK joints
    'Pitch', 'Elbow', 'Wrist_Pitch',
    // Panda IK joints (all 7 arm actuators)
    'actuator1', 'actuator2', 'actuator3', 'actuator4', 'actuator5', 'actuator6', 'actuator7'
  ];
  
  const addActuators = (model, data, params) => {
    let act_range = model.actuator_ctrlrange;
    let actuatorGUIs = [];
    for (let i = 0; i < model.nu; i++) {
      if (!model.actuator_ctrllimited[i]) { continue; }
      let name = textDecoder.decode(
        parentContext.model.names.subarray(
          parentContext.model.name_actuatoradr[i])).split(nullChar)[0];

      parentContext.params[name] = 0.0;
      
      // Use step size of 0.01 for better precision control
      let actuatorGUI = actuatorFolder.add(parentContext.params, name, act_range[2 * i], act_range[2 * i + 1], 0.01).name(name).listen();
      
      // Add note for IK-controlled joints and disable them
      const isIKControlled = ikControlledJoints.includes(name);
      if (isIKControlled) {
        actuatorGUI.name(name + ' (IK)');
      }
      
      actuatorGUIs.push(actuatorGUI);
      actuatorGUI.onChange((value) => {
        // Don't allow changes to IK-controlled joints
        if (isIKControlled) {
          return;
        }
        // Round to 2 decimal places for display and control
        const rounded = Math.round(value * 100) / 100;
        parentContext.params[name] = rounded;
        data.ctrl[i] = rounded;
      });
      
      // Disable after onChange to ensure it's applied
      if (isIKControlled) {
        actuatorGUI.disable();
      }
    }
    return actuatorGUIs;
  };
  let actuatorGUIs = addActuators(parentContext.model, parentContext.data, parentContext.params);
  parentContext.updateGUICallbacks.push((model, data, params) => {
    for (let i = 0; i < actuatorGUIs.length; i++) {
      actuatorGUIs[i].destroy();
    }
    actuatorGUIs = addActuators(model, data, parentContext.params);
  });
  actuatorFolder.close();

  // Add Policy Control folder
  let policyFolder = simulationFolder.addFolder("Policy");
  let motionController = null;
  let policyStatusLabel = null;

  // Policy params
  parentContext.params.policyMotion = '';
  parentContext.params.policyStatus = 'Not Loaded';

  // Status display (read-only)
  policyStatusLabel = policyFolder.add(parentContext.params, 'policyStatus').name('Status').disable();

  // Load/Disable Policy button
  const policyToggle = {
    togglePolicy: async () => {
      if (parentContext.policyEnabled) {
        // Disable policy
        parentContext.disablePolicy();
        parentContext.params.policyStatus = 'Disabled';
        policyStatusLabel.updateDisplay();
        if (motionController) {
          motionController.domElement.style.display = 'none';
        }
      } else {
        // Load policy
        parentContext.params.policyStatus = 'Loading...';
        policyStatusLabel.updateDisplay();
        try {
          const success = await parentContext.loadPolicy('./assets/policies/g1/policy.json');
          if (success) {
            parentContext.params.policyStatus = 'Running';
            policyStatusLabel.updateDisplay();
            // Update motion options
            updateMotionOptions();
            if (motionController) {
              motionController.domElement.style.display = '';
            }
          } else {
            parentContext.params.policyStatus = 'Load Failed';
            policyStatusLabel.updateDisplay();
          }
        } catch (err) {
          console.error('Policy load error:', err);
          parentContext.params.policyStatus = 'Error';
          policyStatusLabel.updateDisplay();
        }
      }
    }
  };
  policyFolder.add(policyToggle, 'togglePolicy').name('Load / Disable Policy');

  // Motion selector dropdown
  const motionOptions = { '-- Select Motion --': '' };
  motionController = policyFolder.add(parentContext.params, 'policyMotion', motionOptions).name('Motion');
  motionController.onChange((value) => {
    if (value && parentContext.policyEnabled) {
      parentContext.requestMotion(value);
    }
  });
  motionController.domElement.style.display = 'none'; // Hidden until policy loaded

  // Update motion options from policy
  const updateMotionOptions = () => {
    const motions = parentContext.getAvailableMotions();
    const newOptions = { '-- Select Motion --': '' };
    for (const motion of motions) {
      newOptions[motion] = motion;
    }
    // Recreate the controller with new options
    const parent = motionController.domElement.parentElement.parentElement;
    motionController.destroy();
    motionController = policyFolder.add(parentContext.params, 'policyMotion', newOptions).name('Motion');
    motionController.onChange((value) => {
      if (value && parentContext.policyEnabled) {
        parentContext.requestMotion(value);
      }
    });
  };

  // Reset button
  const policyReset = {
    resetPolicy: () => {
      parentContext.resetPolicy();
      // Also reset the simulation
      if (parentContext.data && parentContext.mujoco) {
        parentContext.mujoco.mj_resetData(parentContext.model, parentContext.data);
        parentContext.mujoco.mj_forward(parentContext.model, parentContext.data);
      }
    }
  };
  policyFolder.add(policyReset, 'resetPolicy').name('Reset');

  policyFolder.close();

  // Auto-load policy for G1 robot
  const autoLoadPolicyForRobot = async (robotName) => {
    // Disable existing policy first
    if (parentContext.policyEnabled) {
      parentContext.disablePolicy();
    }

    // Auto-load policy for G1
    if (robotName === 'g1') {
      parentContext.params.policyStatus = 'Loading...';
      policyStatusLabel.updateDisplay();
      try {
        const success = await parentContext.loadPolicy('./assets/policies/g1/policy.json');
        if (success) {
          parentContext.params.policyStatus = 'Running';
          policyStatusLabel.updateDisplay();
          updateMotionOptions();
          if (motionController) {
            motionController.domElement.style.display = '';
          }
          policyFolder.open();
        } else {
          parentContext.params.policyStatus = 'Load Failed';
          policyStatusLabel.updateDisplay();
        }
      } catch (err) {
        console.error('Auto policy load error:', err);
        parentContext.params.policyStatus = 'Error';
        policyStatusLabel.updateDisplay();
      }
    } else {
      // Reset status for non-G1 robots
      parentContext.params.policyStatus = 'Not Loaded';
      policyStatusLabel.updateDisplay();
      if (motionController) {
        motionController.domElement.style.display = 'none';
      }
    }
  };

  // Register callback to auto-load policy when robot changes
  parentContext.updateGUICallbacks.push(async (model, data, params) => {
    await autoLoadPolicyForRobot(params.robot);
  });

  // Add Keyboard Controls folder (only shown for scenes with keyboard config)
  let keyboardFolder = null;
  let keyboardLabel = null;

  const setupKeyboardControls = async (model, data, params) => {
    // Remove existing folder if any
    if (keyboardFolder) {
      keyboardFolder.destroy();
      keyboardFolder = null;
      keyboardLabel = null;
    }

    // Check if current robot has keyboard control config
    if (keyboardController.hasConfig(params.robot)) {
      await keyboardController.enable(params.robot, model, data, parentContext.mujoco);

      keyboardFolder = simulationFolder.addFolder("Keyboard Controls");
      // Add description labels - support multi-line descriptions
      const desc = keyboardController.getDescription();
      const lines = desc.split('\n');
      keyboardLabel = [];
      for (let i = 0; i < lines.length; i++) {
        const label = keyboardFolder.add({ info: lines[i] }, 'info').name('').disable();
        // Hide the label column to use full width for the value
        const labelDom = label.domElement.querySelector('.name');
        if (labelDom) labelDom.style.display = 'none';
        const valueDom = label.domElement.querySelector('.widget');
        if (valueDom) valueDom.style.width = '100%';
        keyboardLabel.push(label);
      }
      keyboardFolder.open();
    } else {
      keyboardController.disable();
    }
  };

  // Setup for initial scene
  await setupKeyboardControls(parentContext.model, parentContext.data, parentContext.params);

  // Update when scene changes (async callback)
  parentContext.updateGUICallbacks.push(async (model, data, params) => {
    await setupKeyboardControls(model, data, params);
  });

  // Add function that resets the camera to the default position.
  // Can be triggered by pressing ctrl + A.
  document.addEventListener('keydown', (event) => {
    if (event.ctrlKey && event.code === 'KeyA') {
      // TODO: Use free camera parameters from MuJoCo
      parentContext.camera.position.set(0.5, 1.7, -3);
      parentContext.controls.target.set(0, 0.7, 0);
      parentContext.controls.update(); 
      event.preventDefault();
    }
  });
  actionInnerHTML += 'Reset free camera<br>';
  keyInnerHTML += 'Ctrl A<br>';

  parentContext.gui.open();
}


/** Loads a scene for MuJoCo
 * @param {mujoco} mujoco This is a reference to the mujoco namespace object
 * @param {string} filename This is the name of the .xml file in the /working/ directory of the MuJoCo/Emscripten Virtual File System
 * @param {MuJoCoDemo} parent The three.js Scene Object to add the MuJoCo model elements to
 */
export async function loadSceneFromURL(mujoco, filename, parent) {
    // Free the old data.
    if (parent.data != null) {
      parent.data.delete();
      parent.model = null;
      parent.data  = null;
    }

    // Load in the state from XML.
    const xmlPath = "/working/"+filename;
    console.log('Loading MuJoCo model from:', xmlPath);

    // Debug: Check if the XML file exists and print its content
    try {
      const xmlContent = mujoco.FS.readFile(xmlPath, { encoding: 'utf8' });
      console.log('XML file content (first 500 chars):', xmlContent.substring(0, 500));
    } catch (e) {
      console.error('Failed to read XML file:', e);
    }

    try {
      parent.model = mujoco.MjModel.loadFromXML(xmlPath);
    } catch (e) {
      console.error('MuJoCo XML load error:', e);
      console.error('Error message:', e.message);
      // Try to get more info from MuJoCo's error handling
      if (mujoco.mjXError) {
        console.error('MuJoCo error details:', mujoco.mjXError);
      }
      throw e;
    }
    parent.data  = new mujoco.MjData(parent.model);

    let model = parent.model;
    let data = parent.data;

    // Decode the null-terminated string names.
    let textDecoder = new TextDecoder("utf-8");
    let names_array = new Uint8Array(model.names);
    let fullString = textDecoder.decode(model.names);
    let names = fullString.split(textDecoder.decode(new ArrayBuffer(1)));

    // Create the root object.
    let mujocoRoot = new THREE.Group();
    mujocoRoot.name = "MuJoCo Root";
    parent.scene.add(mujocoRoot);

    /** @type {Object.<number, THREE.Group>} */
    let bodies = {};
    /** @type {Object.<number, THREE.BufferGeometry>} */
    let meshes = {};
    /** @type {THREE.Light[]} */
    let lights = [];

    // Default material definition.
    let material = new THREE.MeshPhysicalMaterial();
    material.color = new THREE.Color(1, 1, 1);
    
    // Loop through the MuJoCo geoms and recreate them in three.js.
    for (let g = 0; g < model.ngeom; g++) {
      // Only visualize geom groups up to 2 (same default behavior as simulate).
      if (!(model.geom_group[g] < 3)) { continue; }

      // Get the body ID and type of the geom.
      let b    = model.geom_bodyid[g];
      let type = model.geom_type  [g];
      let size = [
        model.geom_size[(g*3) + 0],
        model.geom_size[(g*3) + 1],
        model.geom_size[(g*3) + 2]
      ];

      // Create the body if it doesn't exist.
      if (!(b in bodies)) {
        bodies[b] = new THREE.Group();
        
        let start_idx = model.name_bodyadr[b];
        let end_idx = start_idx;
        while (end_idx < names_array.length && names_array[end_idx] !== 0) {
          end_idx++;
        }
        let name_buffer = names_array.subarray(start_idx, end_idx);
        bodies[b].name = textDecoder.decode(name_buffer);
        
        bodies[b].bodyID = b;
        bodies[b].has_custom_mesh = false;
      }

      // Set the default geometry. In MuJoCo, this is a sphere.
      let geometry = new THREE.SphereGeometry(size[0] * 0.5);
      if (type == mujoco.mjtGeom.mjGEOM_PLANE.value) {
        // Special handling for plane later.
      } else if (type == mujoco.mjtGeom.mjGEOM_HFIELD.value) {
        // TODO: Implement this.
      } else if (type == mujoco.mjtGeom.mjGEOM_SPHERE.value) {
        geometry = new THREE.SphereGeometry(size[0]);
      } else if (type == mujoco.mjtGeom.mjGEOM_CAPSULE.value) {
        geometry = new THREE.CapsuleGeometry(size[0], size[1] * 2.0, 20, 20);
      } else if (type == mujoco.mjtGeom.mjGEOM_ELLIPSOID.value) {
        geometry = new THREE.SphereGeometry(1); // Stretch this below
      } else if (type == mujoco.mjtGeom.mjGEOM_CYLINDER.value) {
        geometry = new THREE.CylinderGeometry(size[0], size[0], size[1] * 2.0);
      } else if (type == mujoco.mjtGeom.mjGEOM_BOX.value) {
        geometry = new THREE.BoxGeometry(size[0] * 2.0, size[2] * 2.0, size[1] * 2.0);
      } else if (type == mujoco.mjtGeom.mjGEOM_MESH.value) {
        let meshID = model.geom_dataid[g];

        if (!(meshID in meshes)) {
          geometry = new THREE.BufferGeometry();

          let vertex_buffer = model.mesh_vert.subarray(
             model.mesh_vertadr[meshID] * 3,
            (model.mesh_vertadr[meshID]  + model.mesh_vertnum[meshID]) * 3);
          for (let v = 0; v < vertex_buffer.length; v+=3){
            //vertex_buffer[v + 0] =  vertex_buffer[v + 0];
            let temp             =  vertex_buffer[v + 1];
            vertex_buffer[v + 1] =  vertex_buffer[v + 2];
            vertex_buffer[v + 2] = -temp;
          }

          let normal_buffer = model.mesh_normal.subarray(
             model.mesh_normaladr[meshID] * 3,
            (model.mesh_normaladr[meshID]  + model.mesh_normalnum[meshID]) * 3);
          for (let v = 0; v < normal_buffer.length; v+=3){
            //normal_buffer[v + 0] =  normal_buffer[v + 0];
            let temp             =  normal_buffer[v + 1];
            normal_buffer[v + 1] =  normal_buffer[v + 2];
            normal_buffer[v + 2] = -temp;
          }

          let uv_buffer = model.mesh_texcoord.subarray(
             model.mesh_texcoordadr[meshID] * 2,
            (model.mesh_texcoordadr[meshID]  + model.mesh_texcoordnum[meshID]) * 2);

          let face_to_vertex_buffer = model.mesh_face.subarray(
             model.mesh_faceadr[meshID] * 3,
            (model.mesh_faceadr[meshID]  + model.mesh_facenum[meshID]) * 3);
          let face_to_uv_buffer = model.mesh_facetexcoord.subarray(
             model.mesh_faceadr[meshID] * 3,
            (model.mesh_faceadr[meshID]  + model.mesh_facenum[meshID]) * 3);
          let face_to_normal_buffer = model.mesh_facenormal.subarray(
             model.mesh_faceadr[meshID] * 3,
            (model.mesh_faceadr[meshID]  + model.mesh_facenum[meshID]) * 3);

          // The UV and Normal Buffers are actually indexed by the triangle indices through the face_to_uv_buffer and face_to_normal_buffer.
          // We need to swizzle them into a per-vertex format for three.js
          let swizzled_uv_buffer      = new Float32Array((vertex_buffer.length / 3) * 2);
          let swizzled_normal_buffer  = new Float32Array(vertex_buffer.length);
          for (let t = 0; t < face_to_vertex_buffer.length / 3; t++) {
            let vi0 = face_to_vertex_buffer[(t * 3) + 0];
            let vi1 = face_to_vertex_buffer[(t * 3) + 1];
            let vi2 = face_to_vertex_buffer[(t * 3) + 2];
            let uvi0 = face_to_uv_buffer[(t * 3) + 0];
            let uvi1 = face_to_uv_buffer[(t * 3) + 1];
            let uvi2 = face_to_uv_buffer[(t * 3) + 2];
            let nvi0 = face_to_normal_buffer[(t * 3) + 0];
            let nvi1 = face_to_normal_buffer[(t * 3) + 1];
            let nvi2 = face_to_normal_buffer[(t * 3) + 2];
            swizzled_uv_buffer[(vi0 * 2) + 0] = uv_buffer[(uvi0 * 2) + 0];
            swizzled_uv_buffer[(vi0 * 2) + 1] = uv_buffer[(uvi0 * 2) + 1];
            swizzled_uv_buffer[(vi1 * 2) + 0] = uv_buffer[(uvi1 * 2) + 0];
            swizzled_uv_buffer[(vi1 * 2) + 1] = uv_buffer[(uvi1 * 2) + 1];
            swizzled_uv_buffer[(vi2 * 2) + 0] = uv_buffer[(uvi2 * 2) + 0];
            swizzled_uv_buffer[(vi2 * 2) + 1] = uv_buffer[(uvi2 * 2) + 1];
            swizzled_normal_buffer[(vi0 * 3) + 0] = normal_buffer[(nvi0 * 3) + 0];
            swizzled_normal_buffer[(vi0 * 3) + 1] = normal_buffer[(nvi0 * 3) + 1];
            swizzled_normal_buffer[(vi0 * 3) + 2] = normal_buffer[(nvi0 * 3) + 2];
            swizzled_normal_buffer[(vi1 * 3) + 0] = normal_buffer[(nvi1 * 3) + 0];
            swizzled_normal_buffer[(vi1 * 3) + 1] = normal_buffer[(nvi1 * 3) + 1];
            swizzled_normal_buffer[(vi1 * 3) + 2] = normal_buffer[(nvi1 * 3) + 2];
            swizzled_normal_buffer[(vi2 * 3) + 0] = normal_buffer[(nvi2 * 3) + 0];
            swizzled_normal_buffer[(vi2 * 3) + 1] = normal_buffer[(nvi2 * 3) + 1];
            swizzled_normal_buffer[(vi2 * 3) + 2] = normal_buffer[(nvi2 * 3) + 2];
          }
          geometry.setAttribute("position", new THREE.BufferAttribute(vertex_buffer, 3));
          geometry.setAttribute("normal"  , new THREE.BufferAttribute(swizzled_normal_buffer, 3));
          geometry.setAttribute("uv"      , new THREE.BufferAttribute(swizzled_uv_buffer, 2));
          geometry.setIndex    (Array.from(face_to_vertex_buffer));
          geometry.computeVertexNormals(); // MuJoCo Normals acting strangely... just recompute them
          meshes[meshID] = geometry;
        } else {
          geometry = meshes[meshID];
        }

        bodies[b].has_custom_mesh = true;
      }
      // Done with geometry creation.

      // Set the Material Properties of incoming bodies
      let texture = undefined;
      let color = [
        model.geom_rgba[(g * 4) + 0],
        model.geom_rgba[(g * 4) + 1],
        model.geom_rgba[(g * 4) + 2],
        model.geom_rgba[(g * 4) + 3]];
      if (model.geom_matid[g] != -1) {
        let matId = model.geom_matid[g];
        color = [
          model.mat_rgba[(matId * 4) + 0],
          model.mat_rgba[(matId * 4) + 1],
          model.mat_rgba[(matId * 4) + 2],
          model.mat_rgba[(matId * 4) + 3]];

        // Construct Texture from model.tex_data
        texture = undefined;
        // mat_texid is now a matrix (nmat x mjNTEXROLE)
        // We use mjTEXROLE_RGB (value 1) for standard diffuse/color textures
        const mjNTEXROLE = 10; // Total number of texture roles
        const mjTEXROLE_RGB = 1; // RGB texture role
        let texId = model.mat_texid[(matId * mjNTEXROLE) + mjTEXROLE_RGB];
        
        if (texId != -1) {
          let width    = model.tex_width [texId];
          let height   = model.tex_height[texId];
          let offset   = model.tex_adr   [texId];
          let channels = model.tex_nchannel[texId];
          let texData  = model.tex_data;
          let rgbaArray = new Uint8Array(width * height * 4);
          for (let p = 0; p < width * height; p++){
            rgbaArray[(p * 4) + 0] = texData[offset + ((p * channels) + 0)];
            rgbaArray[(p * 4) + 1] = channels > 1 ? texData[offset + ((p * channels) + 1)] : rgbaArray[(p * 4) + 0];
            rgbaArray[(p * 4) + 2] = channels > 2 ? texData[offset + ((p * channels) + 2)] : rgbaArray[(p * 4) + 0];
            rgbaArray[(p * 4) + 3] = channels > 3 ? texData[offset + ((p * channels) + 3)] : 255;
          }
          texture = new THREE.DataTexture(rgbaArray, width, height, THREE.RGBAFormat, THREE.UnsignedByteType);
          if (texId == 2) {
            texture.repeat = new THREE.Vector2(50, 50);
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
          } else {
            texture.repeat = new THREE.Vector2(model.mat_texrepeat[(model.geom_matid[g] * 2) + 0],
                                               model.mat_texrepeat[(model.geom_matid[g] * 2) + 1]);
            texture.wrapS = THREE.RepeatWrapping;
            texture.wrapT = THREE.RepeatWrapping;
          }

          texture.needsUpdate = true;
        }
      }

      // ===== 修改：使用 Toon 材质实现 NPR 风格 =====
      let currentMaterial = new THREE.MeshToonMaterial({
        color: new THREE.Color(color[0], color[1], color[2]),
        transparent: color[3] < 1.0,
        opacity: color[3] < 1.0 ? color[3] : 1.0,
        map: texture
      });

      let mesh;// = new THREE.Mesh();
      if (type == 0) {
        mesh = new Reflector( new THREE.PlaneGeometry( 100, 100 ), { clipBias: 0.003, texture: texture } );
        mesh.rotateX( - Math.PI / 2 );
      } else {
        mesh = new THREE.Mesh(geometry, currentMaterial);
      }

      mesh.castShadow = g == 0 ? false : true;
      mesh.receiveShadow = type != 7;
      mesh.bodyID = b;
      bodies[b].add(mesh);
      getPosition  (model.geom_pos, g, mesh.position  );
      if (type != 0) { getQuaternion(model.geom_quat, g, mesh.quaternion); }
      if (type == 4) { mesh.scale.set(size[0], size[2], size[1]); } // Stretch the Ellipsoid
    }

    // Parse tendons.
    let tendonMat = new THREE.MeshPhongMaterial();
    tendonMat.color = new THREE.Color(0.8, 0.3, 0.3);
    mujocoRoot.cylinders = new THREE.InstancedMesh(
        new THREE.CylinderGeometry(1, 1, 1),
        tendonMat, 1023);
    mujocoRoot.cylinders.receiveShadow = true;
    mujocoRoot.cylinders.castShadow    = true;
    mujocoRoot.add(mujocoRoot.cylinders);
    mujocoRoot.spheres = new THREE.InstancedMesh(
        new THREE.SphereGeometry(1, 10, 10),
        tendonMat, 1023);
    mujocoRoot.spheres.receiveShadow = true;
    mujocoRoot.spheres.castShadow    = true;
    mujocoRoot.add(mujocoRoot.spheres);

    // Parse lights.
    for (let l = 0; l < model.nlight; l++) {
      let light = new THREE.DirectionalLight();
      if (model.light_type[l] == 0) {
        light = new THREE.SpotLight();
        light.angle = 1.51;//model.light_cutoffangle[l];
      } else if (model.light_type[l] == 1) {
        light = new THREE.DirectionalLight();
      } else if (model.light_type[l] == 2) {
        light = new THREE.PointLight();
      }else if (model.light_type[l] == 3) {
        light = new THREE.HemisphereLight();
      }

      light.angle = 1.11;

      light.decay = model.light_attenuation[l] * 100;
      light.penumbra = 0.5;
      light.castShadow = true; // default false
      light.intensity = light.intensity * 3.14 * 1.0;

      light.shadow.mapSize.width = 1024; // default
      light.shadow.mapSize.height = 1024; // default
      light.shadow.camera.near = 0.1; // default
      light.shadow.camera.far = 10; // default
      //bodies[model.light_bodyid()].add(light);
      if (bodies[0]) {
        bodies[0].add(light);
      } else {
        mujocoRoot.add(light);
      }
      lights.push(light);
    }
    if (model.nlight == 0) {
      let light = new THREE.DirectionalLight();
      mujocoRoot.add(light);
    }

    for (let b = 0; b < model.nbody; b++) {
      //let parent_body = model.body_parentid()[b];
      if (b == 0 || !bodies[0]) {
        mujocoRoot.add(bodies[b]);
      } else if(bodies[b]){
        bodies[0].add(bodies[b]);
      } else {
        console.log("Body without Geometry detected; adding to bodies", b, bodies[b]);
        bodies[b] = new THREE.Group(); bodies[b].name = names[b + 1]; bodies[b].bodyID = b; bodies[b].has_custom_mesh = false;
        bodies[0].add(bodies[b]);
      }
    }
  
    parent.mujocoRoot = mujocoRoot;

    return [model, data, bodies, lights];
}

export function drawTendonsAndFlex(mujocoRoot, model, data) {
  // Update tendon transforms.
  let identityQuat = new THREE.Quaternion();
  let numWraps = 0;
  if (mujocoRoot && mujocoRoot.cylinders) {
    let mat = new THREE.Matrix4();
    for (let t = 0; t < model.ntendon; t++) {
      let startW = data.ten_wrapadr[t];
      let r = model.tendon_width[t];
      for (let w = startW; w < startW + data.ten_wrapnum[t] -1 ; w++) {
        let tendonStart = getPosition(data.wrap_xpos, w    , new THREE.Vector3());
        let tendonEnd   = getPosition(data.wrap_xpos, w + 1, new THREE.Vector3());
        let tendonAvg   = new THREE.Vector3().addVectors(tendonStart, tendonEnd).multiplyScalar(0.5);

        let validStart = tendonStart.length() > 0.01;
        let validEnd   = tendonEnd  .length() > 0.01;

        if (validStart) { mujocoRoot.spheres.setMatrixAt(numWraps    , mat.compose(tendonStart, identityQuat, new THREE.Vector3(r, r, r))); }
        if (validEnd  ) { mujocoRoot.spheres.setMatrixAt(numWraps + 1, mat.compose(tendonEnd  , identityQuat, new THREE.Vector3(r, r, r))); }
        if (validStart && validEnd) {
          mat.compose(tendonAvg, identityQuat.setFromUnitVectors(
            new THREE.Vector3(0, 1, 0), tendonEnd.clone().sub(tendonStart).normalize()),
            new THREE.Vector3(r, tendonStart.distanceTo(tendonEnd), r));
          mujocoRoot.cylinders.setMatrixAt(numWraps, mat);
          numWraps++;
        }
      }
    }

    let curFlexSphereInd = numWraps;
    let tempvertPos = new THREE.Vector3();
    let tempvertRad = new THREE.Vector3();
    for (let i = 0; i < model.nflex; i++) {
      for(let j = 0; j < model.flex_vertnum[i]; j++) {
        let vertIndex = model.flex_vertadr[i] + j;
        getPosition(data.flexvert_xpos, vertIndex, tempvertPos);
        let r   = 0.01;
        mat.compose(tempvertPos, identityQuat, tempvertRad.set(r, r, r));

        mujocoRoot.spheres.setMatrixAt(curFlexSphereInd, mat);
        curFlexSphereInd++;
      }
    }
    mujocoRoot.cylinders.count = numWraps;
    mujocoRoot.spheres  .count = curFlexSphereInd;
    mujocoRoot.cylinders.instanceMatrix.needsUpdate = true;
    mujocoRoot.spheres  .instanceMatrix.needsUpdate = true;
  }
}

// Track which robot directories have been downloaded
const downloadedRobots = new Set();

/**
 * Check if a robot directory has been downloaded
 * @param {string} robotDir - Robot directory name (e.g., 'xlerobot', 'panda')
 * @returns {boolean}
 */
export function isRobotDownloaded(robotDir) {
  return downloadedRobots.has(robotDir);
}

/**
 * Download assets for a specific robot directory (lazy loading)
 * @param {mujoco} mujoco - MuJoCo WASM module
 * @param {string} robotDir - Robot directory name (e.g., 'xlerobot', 'panda', 'humanoid')
 */
export async function downloadRobotAssets(mujoco, robotDir) {
  if (downloadedRobots.has(robotDir)) {
    console.log(`Robot ${robotDir} already downloaded, skipping`);
    return;
  }

  console.log(`Downloading robot assets: ${robotDir}...`);
  const startTime = performance.now();

  // Ensure base robots directory exists
  if (!mujoco.FS.analyzePath('/working/robots').exists) {
    mujoco.FS.mkdir('/working/robots');
  }

  // Load the robot's index.json
  const indexResponse = await fetch(`./assets/robots/${robotDir}/index.json`);
  if (!indexResponse.ok) {
    throw new Error(`Failed to load index.json for robot: ${robotDir}`);
  }
  const files = await indexResponse.json();

  // Normalize paths (Windows to Unix)
  const normalizedFiles = files.map(file => file.replace(/\\/g, "/"));

  // Fetch all files in parallel and fully download their bodies
  const fileDataPromises = normalizedFiles.map(async (file) => {
    const response = await fetch(`./assets/robots/${robotDir}/${file}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${file}: ${response.status}`);
    }
    const lowerFile = file.toLowerCase();
    const isBinary = lowerFile.endsWith(".png") ||
                     lowerFile.endsWith(".stl") ||
                     lowerFile.endsWith(".skn") ||
                     lowerFile.endsWith(".obj") ||
                     lowerFile.endsWith(".ply");
    const data = isBinary
      ? new Uint8Array(await response.arrayBuffer())
      : await response.text();
    return { file, data, isBinary };
  });

  // Wait for ALL files to be fully downloaded (headers AND bodies)
  const filesData = await Promise.all(fileDataPromises);
  console.log(`All ${filesData.length} files downloaded for ${robotDir}, writing to VFS...`);

  // Write files to VFS (now synchronous since all data is in memory)
  for (const { file, data } of filesData) {
    const filePath = `/working/robots/${robotDir}/${file}`;

    // Ensure directories exist
    const parts = file.split("/");
    let working = `/working/robots/${robotDir}`;
    if (!mujoco.FS.analyzePath(working).exists) {
      mujoco.FS.mkdir(working);
    }
    for (let p = 0; p < parts.length - 1; p++) {
      working += "/" + parts[p];
      if (!mujoco.FS.analyzePath(working).exists) {
        mujoco.FS.mkdir(working);
      }
    }

    // Write file to VFS
    mujoco.FS.writeFile(filePath, data);
  }

  downloadedRobots.add(robotDir);
  const elapsed = (performance.now() - startTime).toFixed(0);
  console.log(`Robot ${robotDir} downloaded (${normalizedFiles.length} files, ${elapsed}ms)`);

  // Verify the assets directory exists if there are mesh files
  const hasAssets = normalizedFiles.some(f => f.startsWith('assets/'));
  if (hasAssets) {
    const assetsPath = `/working/robots/${robotDir}/assets`;
    const assetsExists = mujoco.FS.analyzePath(assetsPath).exists;
    console.log(`Robot ${robotDir} assets directory exists: ${assetsExists} (${assetsPath})`);
  }
}

/**
 * Download default robot assets on startup (for fast initial load)
 * @param {mujoco} mujoco - MuJoCo WASM module
 * @param {string} defaultRobotDir - Default robot directory to download
 */
export async function downloadDefaultRobot(mujoco, defaultRobotDir = 'xlerobot') {
  await downloadRobotAssets(mujoco, defaultRobotDir);
}

/**
 * @deprecated Use downloadRobotAssets for lazy loading instead
 * Downloads all robots folder to MuJoCo's virtual filesystem (legacy)
 * @param {mujoco} mujoco
 */
export async function downloadExampleScenesFolder(mujoco) {
  // For backward compatibility, download all known robots
  const robotDirs = ['humanoid', 'panda', 'xlerobot'];
  for (const robotDir of robotDirs) {
    await downloadRobotAssets(mujoco, robotDir);
  }
}

/** Access the vector at index, swizzle for three.js, and apply to the target THREE.Vector3
 * @param {Float32Array|Float64Array} buffer
 * @param {number} index
 * @param {THREE.Vector3} target */
export function getPosition(buffer, index, target, swizzle = true) {
  if (swizzle) {
    return target.set(
       buffer[(index * 3) + 0],
       buffer[(index * 3) + 2],
      -buffer[(index * 3) + 1]);
  } else {
    return target.set(
       buffer[(index * 3) + 0],
       buffer[(index * 3) + 1],
       buffer[(index * 3) + 2]);
  }
}

/** Access the quaternion at index, swizzle for three.js, and apply to the target THREE.Quaternion
 * @param {Float32Array|Float64Array} buffer
 * @param {number} index
 * @param {THREE.Quaternion} target */
export function getQuaternion(buffer, index, target, swizzle = true) {
  if (swizzle) {
    return target.set(
      -buffer[(index * 4) + 1],
      -buffer[(index * 4) + 3],
       buffer[(index * 4) + 2],
      -buffer[(index * 4) + 0]);
  } else {
    return target.set(
       buffer[(index * 4) + 0],
       buffer[(index * 4) + 1],
       buffer[(index * 4) + 2],
       buffer[(index * 4) + 3]);
  }
}

/** Converts this Vector3's Handedness to MuJoCo's Coordinate Handedness
 * @param {THREE.Vector3} target */
export function toMujocoPos(target) { return target.set(target.x, -target.z, target.y); }

/** Standard normal random number generator using Box-Muller transform */
export function standardNormal() {
  return Math.sqrt(-2.0 * Math.log( Math.random())) *
         Math.cos ( 2.0 * Math.PI * Math.random()); }

